// =====================================================================
// Web Push — suscripción de notificaciones para la vendedora.
// El service worker (public/sw.js) ya maneja 'push' y 'notificationclick'.
// Este helper pide permiso, se suscribe con la VAPID public key, y guarda
// la suscripción en push_subscriptions (RLS staff). El backend (push-send)
// dispara la notificación cuando el agente escala un lead a humano.
// =====================================================================

import { supabase } from './supabase';

// VAPID public key de NULED (par en el secret VAPID_PRIVATE_KEY de Supabase)
const VAPID_PUBLIC = 'BJzWTtC0eRKcl-31M9ubSIchYJ87LBFGYWNk_ljH2yYvn2IAeJkG6gioM1YGBXlwMYb4ew-QjB1FKmmW67ewCO0';

function urlB64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function pushSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator
    && typeof window !== 'undefined' && 'PushManager' in window && 'Notification' in window;
}

export async function pushStatus(): Promise<'unsupported' | 'granted' | 'denied' | 'default'> {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission as 'granted' | 'denied' | 'default';
}

export async function enablePushNotifications(): Promise<{ ok: boolean; error?: string }> {
  if (!pushSupported()) {
    return { ok: false, error: 'Este navegador no soporta notificaciones. En iPhone: agrega la app a inicio desde Safari primero.' };
  }
  try {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return { ok: false, error: 'No diste permiso de notificaciones.' };

    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(VAPID_PUBLIC) as BufferSource,
      });
    }
    const j = sub.toJSON();
    const { data: userData } = await supabase.auth.getUser();
    const { error } = await supabase.from('push_subscriptions').upsert({
      user_id: userData?.user?.id ?? null,
      endpoint: sub.endpoint,
      p256dh: j.keys?.p256dh ?? '',
      auth: j.keys?.auth ?? '',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      last_used_at: new Date().toISOString(),
    }, { onConflict: 'endpoint' });
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}
